<template>
  <div class="rating-input">
    <el-select
      v-if="isLabels"
      class="select-primary rating-input__select"
      :value="value"
      clearable
      placeholder="Not rated"
      @change="onSelect"
    >
      <el-option
        v-for="(level, index) in levels"
        :key="index"
        class="select-primary"
        :label="level"
        :value="index + 1"
      />
    </el-select>

    <div
      v-else
      class="rating-input__icons"
      role="slider"
      tabindex="0"
      aria-label="Rating"
      aria-valuemin="0"
      :aria-valuemax="max"
      :aria-valuenow="value == null ? 0 : value"
      :aria-valuetext="formatted"
      @keydown="onKeydown"
      @mouseleave="hover = null"
    >
      <span v-for="n in max" :key="n" class="rating-input__slot">
        <rating-display :value="slotValue(n)" :scale="singleIconScale" :icon="icon" :color="color" size="lg" />
        <button
          v-if="halfSteps"
          type="button"
          tabindex="-1"
          class="rating-input__hit rating-input__hit--left"
          :aria-label="'Rate ' + (n - 0.5)"
          @mouseenter="hover = n - 0.5"
          @click="set(n - 0.5)"
        ></button>
        <button
          type="button"
          tabindex="-1"
          class="rating-input__hit"
          :class="{ 'rating-input__hit--right': halfSteps }"
          :aria-label="'Rate ' + n"
          @mouseenter="hover = n"
          @click="set(n)"
        ></button>
      </span>
      <button
        v-if="value"
        type="button"
        class="rating-input__clear"
        aria-label="Reset rating to 0"
        @click="set(0)"
      >×</button>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'rating-input' }
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { formatRatingValue, iconFill, parseLevels, ratingMax, ratingStep } from '@/util/ratings'

// Editable rating for one scale. Icon scales: click an icon (or its left half when the scale
// allows half steps); arrow keys step, Home clears, End sets the max. Named-level scales: a select.
// v-model: number or null (null = not rated).
const props = defineProps({
  value: { type: Number, default: null },
  scale: { type: Object, required: true },
  icon: { type: String, default: null },
  color: { type: String, default: null },
})

const emit = defineEmits(['input'])

const hover = ref<number | null>(null)

const isLabels = computed(() => props.scale.display_type === 'labels')
const levels = computed(() => parseLevels(props.scale.levels))
const max = computed(() => ratingMax(props.scale as any))
const step = computed(() => ratingStep(props.scale as any))
const halfSteps = computed(() => step.value === 0.5)
const shown = computed(() => (hover.value != null ? hover.value : (props.value || 0)))
const formatted = computed(() => formatRatingValue(props.value || 0, props.scale as any))
// Each slot renders one icon of a one-icon scale, filled by the per-slot value above.
const singleIconScale = computed(() => ({ ...props.scale, display_type: 'icons', max_value: 1 }))

// The n-th slot is a one-icon scale: 1 = full, 0.5 = half, 0 = empty.
function slotValue(n: number) {
  const fill = iconFill(shown.value, n)
  return fill === 'full' ? 1 : fill === 'half' ? 0.5 : 0
}

function set(v: number | null) {
  emit('input', v)
}

function onSelect(v: number | '' | null) {
  emit('input', v === '' || v === null || v === undefined ? null : Number(v))
}

function onKeydown(e: KeyboardEvent) {
  const current = props.value || 0
  let next: number | null | undefined
  if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(max.value, current + step.value)
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(0, current - step.value)
  else if (e.key === 'Home') next = 0
  else if (e.key === 'End') next = max.value
  if (next === undefined) return
  e.preventDefault()
  set(next)
}
</script>

<style lang="scss">
.rating-input {
  // Matches the 0.25em icon spacing of rating-display at its lg size (1.3rem).
  &__icons {
    display: inline-flex;
    align-items: center;
    gap: 0.325rem;
    border-radius: 0.4285rem;
    outline: none;

    &:focus-visible {
      box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.5);
    }
  }

  &__slot {
    position: relative;
    display: inline-flex;
  }

  &__hit {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    &--left { right: 50%; z-index: 1; }
    &--right { left: 50%; }
  }

  &__clear {
    margin-left: 4px;
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: rgba(125, 125, 125, 0.9);
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
  }

  &__select {
    width: 100%;
  }
}
</style>
